import { c as _c } from "react/compiler-runtime";
// Tremor Raw Button [v0.0.0]
import { Slot, Slottable } from "@radix-ui/react-slot";
import { type VariantProps } from "tailwind-variants";
import React from "react";
import { RiLoader2Fill } from "react-icons/ri";
import Tooltip from "@/components/ui/tooltip";
import { tv } from "@/lib/tailwind-utils";
import { cx, focusRing } from "@/lib/tailwind-utils";
const buttonVariants = tv({
  base: [
  // base
  "relative inline-flex items-center justify-center rounded-xl border text-center font-medium shadow-sm transition-all duration-100 ease-in-out active:scale-95", "disabled:cursor-not-allowed disabled:shadow-none disabled:active:scale-100",
  // focus
  focusRing],
  variants: {
    variant: {
      primary: [
      // border
      "border-transparent",
      // text color
      "text-primary-foreground",
      // background color
      "bg-primary",
      // hover color
      "md:hover:bg-primary/90",
      // disabled
      "disabled:!bg-muted disabled:text-foreground-muted"],
      ai: [
      // border
      "border-transparent",
      // text color
      "font-[400]",
      // gradient background
      "bg-gradient-to-tr from-decoda-purple to-primary",
      // overlay effect
      "z-0 after:pointer-events-none after:absolute after:inset-px after:-z-10 after:rounded-[10px] after:bg-background/90 after:transition-all after:duration-300",
      // hover overlay tightening
      "md:hover:after:inset-px md:hover:after:bg-background/95",
      // disabled state
      "disabled:opacity-60 disabled:!bg-gradient-to-tr from-decoda-purple to-primary"],
      secondary: [
      // text color
      "text-foreground-secondary",
      // background & border
      "bg-background border-border",
      //hover color
      "md:hover:bg-row-hover",
      // disabled
      "disabled:text-foreground-muted disabled:!bg-background/50"],
      light: [
      // base
      "shadow-none",
      // border
      "border-transparent",
      // text color
      "text-foreground",
      // background color
      "bg-background-tertiary",
      // hover color
      "md:hover:bg-border-secondary",
      // disabled
      "disabled:!bg-muted disabled:text-foreground-muted"],
      ghost: [
      // base
      "shadow-none",
      // border
      "border-transparent",
      // text color
      "text-foreground",
      // hover color
      "bg-transparent md:hover:bg-hover",
      // disabled
      "disabled:text-foreground-muted disabled:!bg-transparent"],
      destructive: [
      // text color
      "text-white",
      // border
      "border-transparent",
      // background color
      "bg-red-500 dark:bg-red-600",
      // hover color
      "md:hover:bg-red-600 dark:md:hover:bg-red-500",
      // disabled
      "disabled:!bg-red-300 disabled:text-white dark:disabled:!bg-red-800"]
    },
    size: {
      base: ["py-1.5 px-2.5 md:px-3 md:py-2 text-body"],
      sm: ["py-1 px-2 md:px-2.5 md:py-1.5 text-caption"],
      icon: ["size-9"],
      "icon-sm": ["size-8"],
      "icon-lg": ["size-10"]
    }
  },
  defaultVariants: {
    variant: "primary",
    size: "base"
  }
});
interface ButtonProps extends React.ComponentPropsWithoutRef<"button">, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
  loadingText?: string;
  icon?: React.ElementType<any> | React.ReactNode;
  tooltip?: React.ReactNode;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((t0, forwardedRef) => {
  const $ = _c(36);
  let asChild;
  let children;
  let className;
  let disabled;
  let icon;
  let loadingText;
  let props;
  let size;
  let t1;
  let tooltip;
  let variant;
  if ($[0] !== t0) {
    ({
      asChild,
      isLoading: t1,
      loadingText,
      className,
      disabled,
      variant,
      size,
      children,
      icon,
      tooltip,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = asChild;
    $[2] = children;
    $[3] = className;
    $[4] = disabled;
    $[5] = icon;
    $[6] = loadingText;
    $[7] = props;
    $[8] = size;
    $[9] = t1;
    $[10] = tooltip;
    $[11] = variant;
  } else {
    asChild = $[1];
    children = $[2];
    className = $[3];
    disabled = $[4];
    icon = $[5];
    loadingText = $[6];
    props = $[7];
    size = $[8];
    t1 = $[9];
    tooltip = $[10];
    variant = $[11];
  }
  const isLoading = t1 === undefined ? false : t1;
  const Component = asChild ? Slot : "button";
  let t2;
  if ($[12] !== className || $[13] !== size || $[14] !== variant) {
    t2 = cx(buttonVariants({
      variant,
      size
    }), className);
    $[12] = className;
    $[13] = size;
    $[14] = variant;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const t3 = disabled || isLoading;
  let t4;
  if ($[16] !== children || $[17] !== icon || $[18] !== isLoading) {
    t4 = icon && React.isValidElement(icon) && !isLoading ? <div className={cx("shrink-0", children && "mr-1.5")}>{icon}</div> : typeof icon === "function" && React.createElement(icon, {
      className: cx("shrink-0", children && "mr-1.5")
    });
    $[16] = children;
    $[17] = icon;
    $[18] = isLoading;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== children || $[21] !== isLoading || $[22] !== loadingText) {
    t5 = isLoading ? <span className="pointer-events-none flex shrink-0 items-center justify-center gap-1.5"><RiLoader2Fill className="size-4 shrink-0 animate-spin" aria-hidden="true" /><span className="sr-only">{loadingText ? loadingText : "Loading"}</span><span>{loadingText ? loadingText : children}</span></span> : <Slottable>{children}</Slottable>;
    $[20] = children;
    $[21] = isLoading;
    $[22] = loadingText;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== Component || $[25] !== forwardedRef || $[26] !== props || $[27] !== t2 || $[28] !== t3 || $[29] !== t4 || $[30] !== t5) {
    t6 = <Component ref={forwardedRef} className={t2} disabled={t3} {...props}>{t4}{t5}</Component>;
    $[24] = Component;
    $[25] = forwardedRef;
    $[26] = props;
    $[27] = t2;
    $[28] = t3;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  const button = t6;
  let t7;
  if ($[32] !== button || $[33] !== disabled || $[34] !== tooltip) {
    t7 = tooltip ? <Tooltip content={tooltip} showArrow={false} className="-mb-1.5" triggerAsChild={true} triggerOnlyHover={true} role="tooltip"><span tabIndex={disabled ? 0 : undefined} className={cx("inline-flex", disabled && "cursor-not-allowed")}>{button}</span></Tooltip> : button;
    $[32] = button;
    $[33] = disabled;
    $[34] = tooltip;
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  return t7;
});
Button.displayName = "Button";
export default Button;
export { buttonVariants, type ButtonProps };